import React, { useState, useEffect } from 'react';
import axios from 'axios';
import QRCode from 'qrcode';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL + '/api';

const OrganizationEvaluationTab = ({ user, organizationId, setMessage }) => {
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [qrLoading, setQrLoading] = useState(true);
  const [stats, setStats] = useState({
    totalReviews: 0,
    averageRating: 0.0,
    googleReviews: 0
  });

  useEffect(() => {
    if (organizationId) {
      generateQRCode();
      fetchOrganizationStats();
    }
  }, [organizationId]);

  const generateQRCode = async () => {
    try {
      setQrLoading(true);
      const feedbackUrl = `${window.location.origin}/organization-feedback/${organizationId}`;
      
      const qrOptions = {
        width: 256,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      };
      
      const qrDataUrl = await QRCode.toDataURL(feedbackUrl, qrOptions);
      setQrCodeUrl(qrDataUrl);
    } catch (error) {
      console.error('Error generating QR code:', error);
    } finally {
      setQrLoading(false);
    }
  };

  const fetchOrganizationStats = async () => {
    try {
      const response = await axios.get(`${API}/organizations/${organizationId}/feedback-stats`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching organization stats:', error);
      // Use default stats if API fails
      setStats({
        totalReviews: 0,
        averageRating: 0.0,
        googleReviews: 0
      });
    }
  };

  const downloadQRCode = () => {
    if (!qrCodeUrl) return;
    
    const link = document.createElement('a');
    link.download = `organization-qr-code-${organizationId}.png`;
    link.href = qrCodeUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    if (setMessage) {
      setMessage({ type: 'success', text: 'QR Code downloaded successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const copyFeedbackLink = async () => {
    const feedbackUrl = `${window.location.origin}/organization-feedback/${organizationId}`;
    try {
      await navigator.clipboard.writeText(feedbackUrl);
      if (setMessage) {
        setMessage({ type: 'success', text: 'Organization evaluation link copied to clipboard!' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      }
    } catch (error) {
      console.error('Failed to copy link:', error);
      if (setMessage) {
        setMessage({ type: 'error', text: 'Failed to copy link to clipboard' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* QR Code Section */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-lg font-medium mb-4 text-left">Organization Evaluation QR Code</h3>
        <p className="text-sm text-gray-600 mb-6 text-left">
          Share this QR code with your customers to collect overall organization feedback
        </p>
        
        <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
          <div className="flex-shrink-0">
            <div className="w-64 h-64 border-2 border-gray-200 rounded-lg flex items-center justify-center bg-white p-4">
              {qrLoading ? (
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                  <p className="text-sm text-gray-500">Generating QR...</p>
                </div>
              ) : qrCodeUrl ? (
                <img 
                  src={qrCodeUrl} 
                  alt="Organization Feedback QR Code"
                  className="w-full h-full object-contain rounded"
                />
              ) : (
                <div className="text-center">
                  <div className="text-4xl mb-2">❌</div>
                  <p className="text-sm text-gray-500">Failed to generate</p>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex-1">
            <h4 className="font-medium mb-2 text-left">How it works:</h4>
            <ul className="text-sm text-gray-600 space-y-1 text-left">
              <li>• Customers scan the QR code to rate your organization</li>
              <li>• Simple form with star rating, name, and mobile number</li>
              <li>• 4-5 stars → Redirected to Google Reviews</li>
              <li>• 1-3 stars → Custom feedback form for improvement</li>
            </ul>
            
            <div className="mt-4 space-y-2 sm:space-y-0 sm:space-x-2 sm:flex">
              <button
                onClick={downloadQRCode}
                disabled={!qrCodeUrl || qrLoading}
                className="w-full sm:w-auto bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                📥 Download QR Code
              </button>
              <button
                onClick={copyFeedbackLink}
                className="w-full sm:w-auto bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
              >
                🔗 Copy Link
              </button>
            </div>
            
            {/* Feedback URL Display */}
            <div className="mt-3 p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-1">Feedback URL:</p>
              <p className="text-sm text-gray-700 font-mono break-all">
                {`${window.location.origin}/organization-feedback/${organizationId}`}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Evaluation Statistics */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-lg font-medium mb-4 text-left">Organization Feedback Statistics</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{stats.totalReviews}</div>
            <div className="text-sm text-gray-600">Total Reviews</div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600">
              ⭐ {stats.averageRating.toFixed(1)}
            </div>
            <div className="text-sm text-gray-600">Average Rating</div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{stats.googleReviews}</div>
            <div className="text-sm text-gray-600">Google Reviews</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizationEvaluationTab;